import React, { useRef, useState } from "react";
import "./TrendLineChart.css";

// 1 series -> single hue, no legend (title names it). 2 series -> fixed
// categorical order, legend + tooltip (dataviz: choosing-a-form.md /
// color-formula.md). Hover crosshair + tooltip per interaction.md.
const SINGLE_COLOR = "#2a78d6";
const CATEGORICAL = ["#2a78d6", "#e07b39"];
const GRID_COLOR = "var(--an-border-soft)";
const FAINT_COLOR = "var(--an-text-faint)";

const WIDTH = 640;
const HEIGHT = 220;
const PAD = { top: 16, right: 16, bottom: 28, left: 16 };

const TrendLineChart = ({ series, xLabels, valueFormatter = (v) => v }) => {
  const [hoverIdx, setHoverIdx] = useState(null);
  const svgRef = useRef(null);

  const hasData = series && series.length > 0 && xLabels && xLabels.length > 0 && series.some((s) => s.values.length > 0);

  if (!hasData) {
    return <div className="trend-empty">No trend data for the current filters.</div>;
  }

  const allValues = series.flatMap((s) => s.values);
  const maxValue = Math.max(...allValues, 1);
  const minValue = Math.min(...allValues, 0);
  const plotWidth = WIDTH - PAD.left - PAD.right;
  const plotHeight = HEIGHT - PAD.top - PAD.bottom;

  const xAt = (idx) => PAD.left + (xLabels.length > 1 ? (idx / (xLabels.length - 1)) * plotWidth : plotWidth / 2);
  const yAt = (value) => {
    const range = maxValue - minValue || 1;
    return PAD.top + plotHeight - ((value - minValue) / range) * plotHeight;
  };

  const handleMouseMove = (e) => {
    const rect = svgRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * WIDTH;
    const ratio = Math.max(0, Math.min(1, (x - PAD.left) / plotWidth));
    setHoverIdx(Math.round(ratio * (xLabels.length - 1)));
  };

  const colorFor = (idx) => (series.length === 1 ? SINGLE_COLOR : CATEGORICAL[idx % CATEGORICAL.length]);

  return (
    <div className="trend-chart">
      {series.length > 1 && (
        <div className="trend-legend">
          {series.map((s, idx) => (
            <span key={s.key} className="trend-legend-item">
              <span className="trend-legend-swatch" style={{ background: colorFor(idx) }} />
              {s.label}
            </span>
          ))}
        </div>
      )}

      <svg
        ref={svgRef}
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        className="trend-svg"
        onMouseMove={handleMouseMove}
        onMouseLeave={() => setHoverIdx(null)}
      >
        {[0, 0.25, 0.5, 0.75, 1].map((step) => (
          <line
            key={step}
            x1={PAD.left}
            x2={WIDTH - PAD.right}
            y1={PAD.top + plotHeight * step}
            y2={PAD.top + plotHeight * step}
            style={{ stroke: GRID_COLOR }}
            strokeWidth="1"
          />
        ))}

        {series.map((s, sIdx) => {
          const points = s.values.map((v, idx) => `${xAt(idx)},${yAt(v)}`).join(" ");
          const color = colorFor(sIdx);
          return (
            <g key={s.key}>
              <polyline points={points} fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
              {s.values.map((v, idx) => (
                <circle key={idx} cx={xAt(idx)} cy={yAt(v)} r={hoverIdx === idx ? 4 : 2.5} fill={color} />
              ))}
            </g>
          );
        })}

        {hoverIdx !== null && (
          <line
            x1={xAt(hoverIdx)}
            x2={xAt(hoverIdx)}
            y1={PAD.top}
            y2={PAD.top + plotHeight}
            style={{ stroke: FAINT_COLOR }}
            strokeWidth="1"
            strokeDasharray="3,3"
          />
        )}

        {xLabels.map((label, idx) => {
          if (idx !== 0 && idx !== xLabels.length - 1 && idx !== hoverIdx) return null;
          return (
            <text key={idx} x={xAt(idx)} y={HEIGHT - 8} fontSize="10.5" style={{ fill: FAINT_COLOR }} textAnchor="middle">
              {label}
            </text>
          );
        })}
      </svg>

      {hoverIdx !== null && (
        <div className="trend-tooltip" style={{ left: `${(xAt(hoverIdx) / WIDTH) * 100}%` }}>
          <strong>{xLabels[hoverIdx]}</strong>
          {series.map((s, idx) => (
            <span key={s.key}>
              <span className="trend-tooltip-swatch" style={{ background: colorFor(idx) }} />
              {s.label}: {valueFormatter(s.values[hoverIdx])}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

export default TrendLineChart;
